import React, { useEffect, useMemo, useState } from "react";
import api, { eur, fmtDate } from "../lib/api";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { FileText, FileDown, Check, RotateCcw, Receipt } from "lucide-react";
import { toast } from "sonner";

export const INVOICE_LABELS = { a_recevoir: "À recevoir", regle: "Réglée", en_retard: "En retard" };
export const INVOICE_STYLES = {
  a_recevoir: "bg-amber-500/10 text-amber-700 border-amber-500/30",
  regle: "bg-primary/15 text-primary border-primary/40",
  en_retard: "bg-destructive/10 text-destructive border-destructive/40",
};

const downloadInvoice = async (inv) => {
  try {
    const res = await api.get(`/invoices/${inv.id}.pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${inv.invoice_number}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  } catch {
    toast.error("Impossible de générer la facture PDF");
  }
};

const FILTERS = [
  { value: "tous", label: "Toutes" },
  { value: "a_recevoir", label: "À recevoir" },
  { value: "en_retard", label: "En retard" },
  { value: "regle", label: "Réglées" },
];

function Stat({ label, value, accent }) {
  return (
    <Card className="p-4">
      <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className={`tabular mt-1 font-display text-2xl font-extrabold ${accent}`}>{value}</p>
    </Card>
  );
}

export const InvoicesManager = () => {
  const [invoices, setInvoices] = useState([]);
  const [filter, setFilter] = useState("tous");

  const load = () => api.get("/invoices").then((r) => setInvoices(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const markPaid = async (inv) => { await api.post(`/invoices/${inv.id}/mark-paid`); toast.success(`${inv.invoice_number} marquée réglée`); load(); };
  const markUnpaid = async (inv) => { await api.post(`/invoices/${inv.id}/mark-unpaid`); toast.success(`${inv.invoice_number} remise à recevoir`); load(); };

  const totals = useMemo(() => {
    const t = { a_recevoir: 0, en_retard: 0, regle: 0 };
    invoices.forEach((i) => { t[i.effective_status] = (t[i.effective_status] || 0) + i.amount; });
    return t;
  }, [invoices]);

  const shown = filter === "tous" ? invoices : invoices.filter((i) => i.effective_status === filter);

  return (
    <Card className="mt-6 overflow-hidden" data-testid="invoices-manager">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border p-4">
        <div className="flex items-center gap-2">
          <Receipt className="h-5 w-5 text-primary" />
          <div>
            <h3 className="font-display font-bold">Facturation — échéance 20 jours</h3>
            <p className="text-xs text-muted-foreground">Une facture est émise automatiquement à la livraison. Suivez les règlements.</p>
          </div>
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-44" data-testid="invoice-filter"><SelectValue /></SelectTrigger>
          <SelectContent>{FILTERS.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-3">
        <Stat label="À recevoir" value={eur(totals.a_recevoir)} accent="text-amber-600" />
        <Stat label="En retard" value={eur(totals.en_retard)} accent="text-destructive" />
        <Stat label="Réglé" value={eur(totals.regle)} accent="text-primary" />
      </div>

      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>N° facture</TableHead>
              <TableHead>Distributeur</TableHead>
              <TableHead>Montant</TableHead>
              <TableHead>Émission</TableHead>
              <TableHead>Échéance</TableHead>
              <TableHead>Statut</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {shown.map((inv) => (
              <TableRow key={inv.id} data-testid={`invoice-row-${inv.id}`}>
                <TableCell className="tabular font-medium">{inv.invoice_number}</TableCell>
                <TableCell>{inv.enseigne} <span className="text-xs text-muted-foreground">· {inv.client_name}</span></TableCell>
                <TableCell className="tabular font-semibold">{eur(inv.amount)}</TableCell>
                <TableCell className="text-sm">{fmtDate(inv.issue_date)}</TableCell>
                <TableCell className="text-sm">{fmtDate(inv.due_date)}</TableCell>
                <TableCell><Badge variant="outline" className={INVOICE_STYLES[inv.effective_status]}>{INVOICE_LABELS[inv.effective_status]}</Badge></TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-2">
                    <Button size="sm" variant="outline" onClick={() => downloadInvoice(inv)} data-testid={`invoice-pdf-${inv.id}`}>
                      <FileDown className="mr-1 h-3.5 w-3.5" /> PDF
                    </Button>
                    {inv.status === "regle" ? (
                      <Button size="sm" variant="ghost" className="text-secondary" onClick={() => markUnpaid(inv)} data-testid={`invoice-unpaid-${inv.id}`}>
                        <RotateCcw className="mr-1 h-3.5 w-3.5" /> À recevoir
                      </Button>
                    ) : (
                      <Button size="sm" onClick={() => markPaid(inv)} data-testid={`invoice-paid-${inv.id}`}>
                        <Check className="mr-1 h-3.5 w-3.5" /> Marquer réglée
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {shown.length === 0 && (
              <TableRow><TableCell colSpan={7} className="py-8 text-center text-muted-foreground">Aucune facture. Les factures apparaissent dès qu'une commande est livrée.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
};

export const ClientInvoices = () => {
  const [invoices, setInvoices] = useState([]);
  useEffect(() => { api.get("/invoices").then((r) => setInvoices(r.data)).catch(() => {}); }, []);
  if (invoices.length === 0) return null;
  return (
    <div className="mt-12" data-testid="client-invoices">
      <h2 className="font-display text-xl font-bold flex items-center gap-2"><FileText className="h-5 w-5 text-primary" /> Mes factures</h2>
      <div className="mt-4 space-y-2">
        {invoices.map((inv) => (
          <div key={inv.id} data-testid={`client-invoice-${inv.id}`} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-card p-4">
            <div>
              <p className="tabular font-semibold">{inv.invoice_number}</p>
              <p className="text-xs text-muted-foreground">Émise le {fmtDate(inv.issue_date)} · échéance {fmtDate(inv.due_date)}</p>
            </div>
            <div className="flex items-center gap-4">
              <Badge variant="outline" className={INVOICE_STYLES[inv.effective_status]}>{INVOICE_LABELS[inv.effective_status]}</Badge>
              <Button size="sm" variant="outline" onClick={() => downloadInvoice(inv)} data-testid={`client-invoice-pdf-${inv.id}`}>
                <FileDown className="mr-1 h-3.5 w-3.5" /> PDF
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
